import type { LocalizableField } from '@/components/atoms/LocalizedFormField';
import type { TranslationObject } from '@/types';

/**
 * EWM-1073: resolve a LocalizableField for DISPLAY.
 *
 * A model authored in one culture only (the LBBW "Softwareimplementierung Agil" model carries
 * de-DE rows and nothing else) still gets an entry for every tenant culture, because
 * `mapToEstimationModel` seeds each one to '' so react-hook-form controls never go uncontrolled.
 * Reading `localizations[locale]` straight therefore renders an empty string, not a missing key,
 * and the row comes out anonymous.
 *
 * Mirrors the fallback order the API already applies to its own resolved `name`
 * (AdminLocalizationService.ResolveLocalizedValue): requested culture -> the first culture that
 * actually has a value -> the refKey, so an item with no translation in any culture is still
 * identifiable instead of blank. Iteration order is the tenant's supported-culture order, since
 * that is the order `mapToEstimationModel` seeds the keys in.
 *
 * Display only. Never feed this back into a form field or a save request: it would write one
 * culture's text into another culture's row.
 */
export const resolveLocalizedText = (
  field: Pick<LocalizableField, 'refKey' | 'localizations'> | undefined,
  locale: string,
): string => {
  const localizations = field?.localizations ?? {};
  return localizations[locale] || Object.values(localizations).find(Boolean) || field?.refKey || '';
};

export const flattenMessages = (
  nestedMessages: TranslationObject | object,
  prefix: string = '',
): Record<string, string> => {
  return Object.keys(nestedMessages).reduce(
    (acc, key) => {
      const value: string = nestedMessages[key as keyof object];
      const prefixedKey = prefix ? `${prefix}.${key}` : key;

      if (typeof value === 'object' && value !== null) {
        Object.assign(acc, flattenMessages(value, prefixedKey));
      } else {
        acc[prefixedKey] = value;
      }

      return acc;
    },
    {} as Record<string, string>,
  );
};
